// Status pills (28 px), document pills (24 px), tags (22 px) — HANDOFF §1/§6.
// Coral is never a status colour.

import type { ReactNode } from 'react';

export type PillTone = 'ok' | 'warn' | 'err' | 'info' | 'neutral';

export interface PillProps {
  tone?: PillTone;
  /** status = 28 px with a 6 px dot · doc = 24 px (document number pills). */
  size?: 'status' | 'doc';
  dot?: boolean;
  children: ReactNode;
  title?: string;
  'data-testid'?: string;
}

export function Pill({ tone = 'neutral', size = 'status', dot, children, title, 'data-testid': testId }: PillProps) {
  const showDot = dot ?? size === 'status';
  return (
    <span className={`e-pill e-pill--${size}`} data-tone={tone} title={title} data-testid={testId}>
      {showDot && <span className="e-pill-dot" aria-hidden="true" />}
      {children}
    </span>
  );
}

export type TagKind = 'auto' | 'manual' | 'import' | 'cash';

const TAGS: Record<TagKind, { label: string; tone: PillTone }> = {
  auto: { label: 'آلي', tone: 'info' },
  manual: { label: 'يدوي', tone: 'warn' },
  import: { label: 'استيراد', tone: 'neutral' },
  cash: { label: 'نقد', tone: 'info' },
};

export interface TagProps {
  /** Journal/source tags with fixed copy: آلي · يدوي · استيراد · نقد. */
  kind?: TagKind;
  tone?: PillTone;
  children?: ReactNode;
}

/** 22 px tag. `<Tag kind="auto"/>` → «آلي» (info). Free tags: `<Tag tone="ok">من الحجوزات</Tag>`. */
export function Tag({ kind, tone, children }: TagProps) {
  const preset = kind ? TAGS[kind] : null;
  return (
    <span className="e-tag" data-tone={tone ?? preset?.tone ?? 'neutral'} data-kind={kind}>
      {children ?? preset?.label}
    </span>
  );
}

export type SyncState = 'synced' | 'pending' | 'local' | 'conflict';

const SYNC: Record<Exclude<SyncState, 'synced'>, { label: string; tone: PillTone }> = {
  pending: { label: 'بانتظار المزامنة', tone: 'warn' },
  local: { label: 'محلي · رقم مؤقت', tone: 'warn' },
  conflict: { label: 'تعارض', tone: 'err' },
};

/** Document sync pill (§6): synced renders NOTHING — the normal state is silent. */
export function SyncPill({ state, size = 'doc' }: { state: SyncState; size?: 'status' | 'doc' }) {
  if (state === 'synced') return null;
  const s = SYNC[state];
  return (
    <Pill tone={s.tone} size={size} dot={false} data-testid={`sync-${state}`}>
      {s.label}
    </Pill>
  );
}
